import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import {
  SlimTable,
  SlimTableBody,
  SlimTableCell,
  SlimTableHead,
  SlimTableHeader,
  SlimTableRow,
} from '@/components/common/SlimTable';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/dashboard/DashboardCard';
import type { ProtocolType, ProtocolTypeItem } from '@/domain/Protocol';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';

/** API can return item as price object with nested product (item.item) or as product directly */
function getItemDisplayName(protocolItem: ProtocolTypeItem): string {
  const item = protocolItem.item as
    | { name?: string; item?: { name?: string } }
    | null
    | undefined;
  if (!item) return '-';
  if (item.item?.name) return item.item.name;
  return item.name ?? '-';
}

function getItemDose(protocolItem: ProtocolTypeItem): string {
  if (protocolItem.dosage?.trim()) return protocolItem.dosage;
  const item = protocolItem.item as
    | { strength?: string; size?: number; unit?: string }
    | null
    | undefined;
  if (item?.strength) return item.strength;
  if (item?.size != null && item.unit) return `${item.size} ${item.unit}`;
  return '-';
}

function getItemPrice(protocolItem: ProtocolTypeItem): string {
  const item = protocolItem.item as
    | { formatted_price?: number | string }
    | null
    | undefined;
  if (item?.formatted_price == null) return '-';
  const price =
    typeof item.formatted_price === 'string'
      ? item.formatted_price
      : String(item.formatted_price);
  return `€${price}`;
}

interface ProtocolTypeCardProps {
  protocolType: ProtocolType;
  selectedItemIds?: Set<number>;
  onToggleItem?: (protocolTypeItem: ProtocolTypeItem) => void;
}

export function ProtocolTypeCard({
  protocolType,
  selectedItemIds = new Set(),
  onToggleItem,
}: ProtocolTypeCardProps) {
  const [showDescriptions, setShowDescriptions] = useState(false);
  const { role } = useHasRole();

  const canModifyItems = !!role && role !== Role.GUEST && role !== Role.PACKER;

  const hasDescriptions = protocolType.items.some((item) =>
    item.description?.trim(),
  );

  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle>
          <Heading as="h3" size="h4">
            {protocolType.name}
          </Heading>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {protocolType.items.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            <SlimTable>
              <SlimTableHeader>
                <SlimTableRow>
                  <SlimTableHead>Supplement</SlimTableHead>
                  <SlimTableHead>Dose</SlimTableHead>
                  <SlimTableHead>Price</SlimTableHead>
                  <SlimTableHead></SlimTableHead>
                </SlimTableRow>
              </SlimTableHeader>
              <SlimTableBody>
                {protocolType.items.map((protocolItem) => {
                  const isAdded = selectedItemIds.has(protocolItem.id);
                  return (
                    <SlimTableRow
                      key={protocolItem.id}
                      className={isAdded ? 'opacity-60' : undefined}
                    >
                      <SlimTableCell>
                        <div>
                          <div>{getItemDisplayName(protocolItem)}</div>
                          {showDescriptions && protocolItem.description && (
                            <div className="text-muted-foreground text-sm">
                              {protocolItem.description}
                            </div>
                          )}
                        </div>
                      </SlimTableCell>
                      <SlimTableCell>{getItemDose(protocolItem)}</SlimTableCell>
                      <SlimTableCell>
                        {getItemPrice(protocolItem)}
                      </SlimTableCell>
                      <SlimTableCell className="text-right">
                        {canModifyItems && onToggleItem && (
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              className={
                                isAdded
                                  ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                                  : 'hover:bg-primary hover:text-primary-foreground'
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onToggleItem(protocolItem);
                              }}
                              aria-pressed={isAdded}
                            >
                              {isAdded ? (
                                <CheckIcon className="size-4" />
                              ) : (
                                <PlusIcon className="size-4" />
                              )}
                            </Button>
                          </div>
                        )}
                      </SlimTableCell>
                    </SlimTableRow>
                  );
                })}
              </SlimTableBody>
            </SlimTable>

            {hasDescriptions && (
              <Button
                variant="outlinePrimary"
                onClick={() => setShowDescriptions((prev) => !prev)}
                className="w-fit"
              >
                {showDescriptions ? 'Read less' : 'Read more'}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground py-4 text-center">
            No items in this protocol type
          </p>
        )}
      </CardContent>
    </Card>
  );
}
